import { useAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';

import { sidebarVisibleAtom } from '@frontend/atoms';
import { MOBILE_BREAKPOINT_PX } from '@frontend/styles/layout.css';

type SidebarVisibilityEvent = CustomEvent<{ isVisible: boolean }>;

function isSidebarVisibilityEvent(event: Event): event is SidebarVisibilityEvent {
  return event instanceof CustomEvent && typeof event.detail?.isVisible === 'boolean';
}

export function useSidebarVisibility() {
  const [sidebarVisible, setSidebarVisible] = useAtom(sidebarVisibleAtom);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT_PX);

  const updateIsMobile = useCallback(() => {
    setIsMobile(window.innerWidth < MOBILE_BREAKPOINT_PX);
  }, []);

  useEffect(() => {
    const handleVisibilityChange = (event: Event) => {
      if (isSidebarVisibilityEvent(event)) {
        setSidebarVisible(event.detail.isVisible);
      }
    };

    document.addEventListener('opal::sidebar::visibilityChanged', handleVisibilityChange);

    const resizeObserver = new ResizeObserver(updateIsMobile);
    resizeObserver.observe(document.body);

    return () => {
      document.removeEventListener('opal::sidebar::visibilityChanged', handleVisibilityChange);
      resizeObserver.disconnect();
    };
  }, [setSidebarVisible, updateIsMobile]);

  return { isSidebarVisibleOnMobile: isMobile && sidebarVisible };
}
